import type { DailyTimesheet, SubmissionValidationError, SubmissionValidationResult } from '../contracts/timesheet.js'
import { calculateHoursWorked } from './timesheet-business-rules.js'

/**
 * Validates a timesheet for submission according to business rules
 * @param timesheet The timesheet to validate
 * @returns Validation result with errors and warnings
 */
export const validateTimesheetForSubmission = async (timesheet: DailyTimesheet): Promise<SubmissionValidationResult> => {
  const errors: SubmissionValidationError[] = []
  const warnings: SubmissionValidationError[] = []

  // Rule 1: At least one worker must be present
  if (!timesheet.entries || timesheet.entries.length === 0) {
    errors.push({
      field: 'entries',
      message: 'At least one worker must be present on the timesheet',
      severity: 'error'
    })
  }

  // Rule 2: All required fields must be populated
  if (!timesheet.payPeriodId) {
    errors.push({
      field: 'payPeriodId',
      message: 'Pay period is required for submission',
      severity: 'error'
    })
  }

  if (!timesheet.jobSiteId) {
    errors.push({
      field: 'jobSiteId',
      message: 'Job site is required for submission',
      severity: 'error'
    })
  }

  // Rule 3 & 4: Validate each worker entry
  for (const entry of timesheet.entries) {
    const workerName = entry.worker?.name || `Worker ${entry.workerId}`

    // Check for complete time entries
    if (!entry.timeIn) {
      errors.push({
        field: 'timeIn',
        workerId: entry.workerId,
        message: `Time in is required for ${workerName}`,
        severity: 'error'
      })
    }

    if (!entry.timeOut) {
      errors.push({
        field: 'timeOut',
        workerId: entry.workerId,
        message: `Time out is required for ${workerName}`,
        severity: 'error'
      })
    }

    // If both times are present, validate hours worked
    if (entry.timeIn && entry.timeOut) {
      const hoursWorked = calculateHoursWorked(entry.timeIn, entry.timeOut)
      
      // Check for excessive hours (>24 hours - should be impossible but safety check)
      if (hoursWorked > 24) {
        errors.push({
          field: 'hoursWorked',
          workerId: entry.workerId,
          message: `${workerName} cannot work more than 24 hours (${hoursWorked.toFixed(1)} hours)`,
          severity: 'error'
        })
      }
      
      // Check for very short shifts (<2 hours - warning)
      if (hoursWorked < 2) {
        warnings.push({
          field: 'hoursWorked',
          workerId: entry.workerId,
          message: `${workerName} worked only ${hoursWorked.toFixed(1)} hours (short shift)`,
          severity: 'warning'
        })
      }
    }
  }

  // Additional business rule validations
  
  // Check for duplicate workers
  const workerIds = timesheet.entries.map(entry => entry.workerId)
  const duplicateWorkerIds = workerIds.filter((id, index) => workerIds.indexOf(id) !== index)
  
  if (duplicateWorkerIds.length > 0) {
    for (const workerId of duplicateWorkerIds) {
      const workerName = timesheet.entries.find(e => e.workerId === workerId)?.worker?.name || `Worker ${workerId}`
      errors.push({
        field: 'workerId',
        workerId,
        message: `${workerName} appears multiple times on this timesheet`,
        severity: 'error'
      })
    }
  }

  // Check total timesheet hours for reasonableness
  const totalHours = timesheet.totalHours || 0
  const expectedMaxHours = timesheet.entries.length * 12 // Assuming max 12 hours per worker
  
  if (totalHours > expectedMaxHours) {
    warnings.push({
      field: 'totalHours',
      message: `Total hours (${totalHours.toFixed(1)}) seems high for ${timesheet.entries.length} workers`,
      severity: 'warning'
    })
  }

  // Check if timesheet is for a future date
  const timesheetDate = new Date(timesheet.date)
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day
  
  if (timesheetDate > today) {
    warnings.push({
      field: 'date',
      message: 'Submitting timesheet for a future date',
      severity: 'warning'
    })
  }

  // Check if timesheet is very old (>30 days)
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  
  if (timesheetDate < thirtyDaysAgo) {
    warnings.push({
      field: 'date',
      message: 'Submitting timesheet for a date more than 30 days ago',
      severity: 'warning'
    })
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  }
}

/**
 * Generates a unique submission ID for a timesheet
 * @param date The timesheet date
 * @returns A unique submission ID in format TS-YYYY-MMDD-XXX
 */
export const generateSubmissionId = (date: string): string => {
  const dateObj = new Date(date)
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  
  // Generate a random 3-digit sequence for now
  // In production, this would query the database for the next sequence
  const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
  
  return `TS-${year}-${month}${day}-${sequence}`
}

/**
 * Estimates basic payroll cost for a timesheet (regular hours only)
 * 
 * Note: Overtime calculation removed - will be implemented later based on
 * work cycles, pay periods, and regulatory requirements.
 * 
 * @param timesheet The timesheet to calculate payroll for
 * @param hourlyRate Optional base hourly rate (defaults to $25)
 * @returns Estimated payroll cost for regular hours only
 */
export const estimateBasicPayroll = (
  timesheet: DailyTimesheet, 
  hourlyRate: number = 25
): number => {
  let totalCost = 0
  
  for (const entry of timesheet.entries) {
    totalCost += entry.hoursWorked * hourlyRate
  }
  
  return Math.round(totalCost * 100) / 100 // Round to 2 decimal places
}

/**
 * Prepares timesheet data for submission
 * @param timesheet The timesheet to prepare
 * @returns Enhanced timesheet with calculated submission fields
 */
export const prepareTimesheetForSubmission = (timesheet: DailyTimesheet): DailyTimesheet => {
  const submissionId = generateSubmissionId(timesheet.date)
  const estimatedPayroll = estimateBasicPayroll(timesheet)
  const submittedAt = new Date().toISOString()
  
  return {
    ...timesheet,
    status: 'submitted',
    submissionId,
    overtimeHours: null, // TODO: Calculate when overtime system is implemented
    estimatedPayroll,
    submittedAt,
    isDraft: false,
    lastSavedAt: null, // No longer a draft
    updatedAt: submittedAt
  }
} 